// NotificationDropdown v2.1 - Fixed title/message display
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Bell, 
  X, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  CheckCircle2,
  Zap,
  ExternalLink
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;

export function NotificationDropdown() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Fetch unread count periodically
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000); // Every minute
    return () => clearInterval(interval);
  }, []);
  
  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const res = await axios.get(`${API}/api/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(res.data.count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };
  
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/notifications/history?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      fetchNotifications();
    }
  };
  
  const handleMarkAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking all read:', error);
    }
  };
  
  const buildNotificationUrl = (notif) => {
    const t = notif.type?.toLowerCase() || '';
    const data = notif.data || {};
    const tradeId = data.trade_id;
    const symbol = data.symbol;
    // Backend now provides a precomputed deep-link URL
    if (notif.url && typeof notif.url === 'string' && notif.url.startsWith('/')) {
      return notif.url;
    }
    if (data.url && typeof data.url === 'string' && data.url.startsWith('/')) {
      return data.url;
    }
    // Trade lifecycle events → History with trade_id (History switches tab paper/real + expands)
    if (
      t.includes('tp1') || t.includes('tp2') || t.includes('tp100') || t.includes('tp_100') ||
      t.includes('sl') || t.includes('liquidation') || t.includes('entry') || t.includes('activated') ||
      t.includes('retracement') || t.includes('24h_warning')
    ) {
      return tradeId ? `/history?trade_id=${encodeURIComponent(tradeId)}` : '/history';
    }
    // AI setup opportunity → analysis page prefilled with symbol
    if (t.includes('setup_alert') || t.includes('autoscan') || t.includes('scan')) {
      return symbol ? `/analysis?symbol=${encodeURIComponent(symbol)}` : '/analysis';
    }
    if (t.includes('analysis')) return '/analysis';
    if (t.includes('dca')) return '/dca';
    if (t.includes('eco')) return '/news-eco';
    if (t.includes('market_open') || t.includes('market_close')) return '/history';
    if (t.includes('gift')) return '/subscribe';
    return '/notifications';
  };

  const handleNotificationClick = async (notif) => {
    // Mark as read
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/notifications/${notif.id || notif._id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount((c) => Math.max(0, c - (notif.read ? 0 : 1)));
    } catch (error) {
      // Ignore
    }
    const url = buildNotificationUrl(notif);
    setIsOpen(false);
    navigate(url);
  };
  
  const getNotificationIcon = (type) => {
    const t = type?.toLowerCase() || '';
    if (t.includes('analysis_complete')) return <CheckCircle2 className="w-4 h-4 text-purple-400" />;
    if (t.includes('analysis_failed')) return <AlertTriangle className="w-4 h-4 text-red-400" />;
    if (t.includes('tp1')) return <TrendingUp className="w-4 h-4 text-emerald-400" />;
    if (t.includes('tp2')) return <TrendingUp className="w-4 h-4 text-emerald-500" />;
    if (t.includes('tp100') || t.includes('tp_100')) return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (t.includes('sl_warning') || t.includes('sl_alert')) return <AlertTriangle className="w-4 h-4 text-amber-400" />;
    if (t.includes('sl_hit') || t.includes('sl')) return <TrendingDown className="w-4 h-4 text-red-400" />;
    if (t.includes('liquidation')) return <AlertTriangle className="w-4 h-4 text-red-500" />;
    if (t.includes('entry') || t.includes('activated')) return <CheckCircle2 className="w-4 h-4 text-blue-400" />;
    if (t.includes('autoscan') || t.includes('scan')) return <Zap className="w-4 h-4 text-amber-400" />;
    return <Bell className="w-4 h-4 text-gray-400" />;
  };
  
  const getNotificationTitle = (type, data) => {
    const t = type?.toLowerCase() || '';
    const symbol = data?.symbol || '';
    const prefix = symbol ? `${symbol} - ` : '';
    
    if (t.includes('analysis_complete')) return `${prefix}Analyse IA prête`;
    if (t.includes('analysis_failed')) return `${prefix}Analyse IA échouée`;
    if (t.includes('tp1_retracement')) return `${prefix}Retour sous TP1`;
    if (t.includes('tp2_retracement')) return `${prefix}Retour sous TP2`;
    if (t.includes('tp1')) return `${prefix}TP1 atteint (25%)`;
    if (t.includes('tp2')) return `${prefix}TP2 atteint (50%)`;
    if (t.includes('tp100') || t.includes('tp_100')) return `${prefix}TP100% atteint!`;
    if (t.includes('sl_warning') || t.includes('sl_alert')) return `${prefix}Alerte Stop Loss`;
    if (t.includes('sl_hit') || (t.includes('sl') && !t.includes('warning'))) return `${prefix}Stop Loss touché`;
    if (t.includes('liquidation_warning')) return `${prefix}Alerte Liquidation`;
    if (t.includes('liquidation')) return `${prefix}Position liquidée`;
    if (t.includes('entry') || t.includes('activated')) return `${prefix}Trade activé`;
    if (t.includes('24h_warning') || t.includes('intraday_24h')) return `${prefix}INTRADAY > 24h`;
    if (t.includes('setup_alert')) return `${prefix}Setup détecté`;
    if (t.includes('autoscan') || t.includes('scan')) return `${prefix}Opportunité détectée`;
    if (t.includes('dca_zone')) return `${prefix}Zone DCA atteinte`;
    if (t.includes('eco_result')) return `${data?.event_name || 'Résultat économique'} publié`;
    if (t.includes('market_open')) return 'Ouverture des marchés';
    if (t.includes('market_close')) return 'Clôture des marchés';
    if (t.includes('gift')) return `Cadeau ${data?.tier ? data.tier.toUpperCase() : ''}`.trim();
    return symbol ? `${symbol} - Notification` : 'Notification';
  };
  
  const getNotificationMessage = (notif) => {
    const data = notif.data || {};
    if (notif.type?.includes('analysis_complete')) {
      return `Swing: ${data.swing_direction || 'N/A'} • Intraday: ${data.intraday_direction || 'N/A'}`;
    }
    if (notif.type?.includes('analysis_failed')) {
      return data.error || 'Relancez l\'analyse';
    }
    if (data.pnl_percent !== undefined) {
      return `PnL: ${data.pnl_percent > 0 ? '+' : ''}${data.pnl_percent.toFixed(2)}%`;
    }
    if (data.sl_progress !== undefined) {
      return `Progression SL: ${data.sl_progress.toFixed(0)}%`;
    }
    if (data.direction && data.entry_price) {
      return `${data.direction} @ ${data.entry_price}`;
    }
    return data.symbol || notif.message || '';
  };
  
  const formatTime = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: fr });
    } catch {
      return '';
    }
  };
  
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-lg hover:bg-muted transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-muted-foreground" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      
      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-card border border-border rounded-xl shadow-2xl overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-foreground font-semibold">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-primary hover:text-primary/80 transition-colors"
                >
                  Tout marquer lu
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded hover:bg-muted transition-colors"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          </div>
          
          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
              </div>
            ) : notifications.length > 0 ? (
              <div className="divide-y divide-border">
                {notifications.map((notif, idx) => (
                  <div
                    key={notif.id || notif._id || idx}
                    onClick={() => handleNotificationClick(notif)}
                    className={`
                      flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors
                      ${notif.read ? 'bg-transparent' : 'bg-primary/5'}
                      hover:bg-muted/50
                    `}
                  >
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notif.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${notif.read ? 'text-muted-foreground' : 'text-foreground font-medium'}`}>
                        {notif.title || getNotificationTitle(notif.type, notif.data)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {getNotificationMessage(notif)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatTime(notif.created_at)}
                      </p>
                    </div>
                    {!notif.read && (
                      <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2"></div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center">
                <Bell className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground text-sm">Aucune notification</p>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="px-4 py-3 border-t border-border">
            <button
              onClick={() => {
                navigate('/notifications');
                setIsOpen(false);
              }}
              className="flex items-center justify-center gap-2 w-full text-sm text-primary hover:text-primary/80 transition-colors"
            >
              Gérer les alertes
              <ExternalLink className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationDropdown;
